import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, Wallet, ArrowRight, Check, Clock, AlertCircle, PlayCircle, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import CatalogPicker from "@/components/CatalogPicker";
import { toast } from "sonner";
import AuroraSection, { Aurora } from "@/components/motion/Aurora";
import RevealTitle from "@/components/motion/RevealTitle";


export default function ClientWelcome() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  // El paso 1 queda bloqueado al enviar el formulario, así que si el pago
  // (último paso del wizard) quedó a medias este es el único camino de vuelta

  // Personalización ANTES de pagar: punto de recogida y devolución, pack de
  // km y extras. La señal se calcula con todo incluido (payment_plan.py);
  // hasta ahora esto solo existía dentro del pre-checkin, ya pasada la señal.
  const [catalogo, setCatalogo] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);

  const paySignal = async () => {
    setPayLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/signal/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setPayLoading(false);
    }
  };

  const payDeposit = async () => {
    setPayLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/checkout/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setPayLoading(false);
    }
  };

  const cargar = useCallback(
    ({ silencioso = false } = {}) =>
      api
        .get(`/r/${code}`)
        .then((res) => {
          setR(res.data);
          // La selección guardada manda: al volver de un pago o recargar, las
          // casillas tienen que aparecer como las dejó el cliente.
          const inicial = {};
          for (const it of res.data.selected_items || []) {
            if (it.product_id) inicial[it.product_id] = it.qty || 1;
          }
          setSeleccion(inicial);
          return res.data;
        })
        .catch((err) => {
          if (silencioso) return null;
          toast.error(formatError(err));
          navigate("/");
        }),
    [code, navigate],
  );

  useEffect(() => {
    cargar();
    api.get("/catalog").then(({ data }) => setCatalogo(data.products || [])).catch(() => {});
  }, [cargar]);

  const guardarSeleccion = async () => {
    setGuardando(true);
    try {
      const items = Object.entries(seleccion)
        .filter(([, q]) => q > 0)
        .map(([product_id, qty]) => ({ product_id, qty }));
      await api.post(`/r/${code}/items`, { items });
      await cargar({ silencioso: true });
      toast.success("Opciones guardadas");
      setAbierto(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!r)
    return (
      <div className="aurora-page relative isolate min-h-screen bg-ivory flex items-center justify-center text-warmgray">
        <Aurora variant="light" fixed />
        Cargando...
      </div>
    );

  const dias = (() => {
    const ms = new Date(r.return_date) - new Date(r.pickup_date);
    return Number.isFinite(ms) ? Math.max(1, Math.round(ms / 86400000)) : 1;
  })();

  const canCheckin = !r.has_checkin;
  const canCheckout = r.has_checkin && !r.has_checkout;
  const allDone = r.has_checkin && r.has_checkout;

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory">
      <Aurora variant="light" fixed />
      {/* Cabecera sobre aurora oscura (antes una foto de 30 s de carga en móvil) */}
      <AuroraSection as="div" variant="dark" className="h-56 sm:h-72 text-white">
        <Link to="/" className="absolute top-4 left-4 text-white/90 text-sm hover:text-white" data-testid="welcome-home-link">
          ← Inicio
        </Link>
        <div className="absolute inset-x-0 bottom-0 p-6 text-white">
          <div className="container-x">
            <span className="text-overline text-warn">Tu reserva · {r.code}</span>
            <RevealTitle as="h1" immediate className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
              {`Hola ${r.client_name.split(" ")[0]} 👋`}
            </RevealTitle>
          </div>
        </div>
      </AuroraSection>

      <main className="container-x py-8 max-w-2xl fade-in">
        <div className="bg-white border border-sand rounded-xl overflow-hidden mb-6">
          <div className="p-5 grid grid-cols-2 gap-4 border-b border-sand">
            <div>
              <div className="text-overline mb-1">Recogida</div>
              <div className="flex items-center gap-2 text-charcoal font-medium">
                <Calendar className="h-4 w-4 text-forest-deep" /> {r.pickup_date}
              </div>
              <div className="text-sm text-warmgray mt-0.5">{r.pickup_time} · {r.pickup_location}</div>
            </div>
            <div>
              <div className="text-overline mb-1">Devolución</div>
              <div className="flex items-center gap-2 text-charcoal font-medium">
                <Calendar className="h-4 w-4 text-terracotta" /> {r.return_date}
              </div>
              <div className="text-sm text-warmgray mt-0.5">{r.return_time} · {r.return_location}</div>
            </div>
          </div>
          <div className="p-5 grid grid-cols-3 gap-4">
            <div>
              <div className="text-overline mb-1">Importe total</div>
              <div className="text-charcoal font-medium">
                {Number(r.total_amount || 0).toFixed(2)} €
              </div>
            </div>
            <div>
              <div className="text-overline mb-1">Fianza</div>
              <div className="flex items-center gap-2 text-charcoal font-medium">
                <Wallet className="h-4 w-4" /> {r.deposit_amount} €
              </div>
            </div>
            <div className="text-right">
              <div className="text-overline mb-1">Km incluidos</div>
              <div className="text-charcoal font-medium">{r.km_included_per_day} / día</div>
            </div>
          </div>
        </div>

        {allDone ? (
          <div className="bg-ok/10 border border-ok/30 rounded-xl p-6 text-center" data-testid="welcome-completed">
            <div className="h-12 w-12 mx-auto rounded-full bg-ok text-white flex items-center justify-center mb-3">
              <Check className="h-6 w-6" />
            </div>
            <h3 className="font-display text-xl font-bold text-charcoal mb-1">¡Todo listo!</h3>
            <p className="text-sm text-warmgray">
              Has completado el check-in y check-out. ¡Esperamos que hayas
              disfrutado de la aventura!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => canCheckin && navigate(`/checkin/${r.code}`)}
              disabled={!canCheckin}
              className={`w-full text-left bg-white border-2 rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 ${
                canCheckin
                  ? "border-forest-deep hover:-translate-y-0.5 hover:shadow-md"
                  : "border-sand opacity-60 cursor-default"
              }`}
              data-testid="welcome-checkin-btn"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                    r.has_checkin ? "bg-ok/15 text-ok" : "bg-forest-deep text-white"
                  }`}
                >
                  {r.has_checkin ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-charcoal">
                    1. Pre-Checkin
                  </div>
                  <div className="text-sm text-warmgray">
                    {r.has_checkin
                      ? "Completado ✓"
                      : "Tus datos para el contrato y firma online"}
                  </div>
                </div>
              </div>
              {!r.has_checkin && <ArrowRight className="h-5 w-5 text-forest-deep shrink-0" />}
            </button>

            {r.self_service && r.has_checkin && !r.has_handover && (
              <button
                onClick={() => navigate(`/recogida/${r.code}`)}
                className="w-full text-left bg-white border-2 border-warn rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                data-testid="welcome-selfpickup-btn"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-warn text-white flex items-center justify-center shrink-0">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-charcoal">
                      Recogida autónoma
                    </div>
                    <div className="text-sm text-warmgray">
                      ¿Ya estás en el punto de recogida? Abre la furgoneta sin
                      esperas desde tu móvil.
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-warn shrink-0" />
              </button>
            )}

            {r.has_checkin && !r.has_checkout && (
              <button
                onClick={() => navigate(`/devolucion/${r.code}`)}
                className="w-full text-left bg-white border-2 border-ok rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                data-testid="welcome-returnprep-btn"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${r.return_checklist_completed ? "bg-ok/15 text-ok" : "bg-ok text-white"}`}>
                    {r.return_checklist_completed ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-charcoal">Preparar la devolución</div>
                    <div className="text-sm text-warmgray">
                      {r.return_checklist_completed
                        ? "Checklist completada ✓ · avisos de retraso o ampliación"
                        : "Checklist de devolución · avisar de retraso o ampliar días"}
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-ok shrink-0" />
              </button>
            )}

            <button
              onClick={() => canCheckout && navigate(`/checkout/${r.code}`)}
              disabled={!canCheckout}
              className={`w-full text-left bg-white border-2 rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 ${
                canCheckout
                  ? "border-terracotta hover:-translate-y-0.5 hover:shadow-md"
                  : "border-sand opacity-60 cursor-default"
              }`}
              data-testid="welcome-checkout-btn"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                    !canCheckout ? "bg-sand-light text-warmgray-light" : "bg-terracotta text-white"
                  }`}
                >
                  {canCheckout ? <ArrowRight className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-charcoal">
                    2. Check-out (devolución)
                  </div>
                  <div className="text-sm text-warmgray">
                    {!r.has_checkin
                      ? "Disponible tras el check-in"
                      : "Estado de devolución, incidencias y firma"}
                  </div>
                </div>
              </div>
              {canCheckout && <ArrowRight className="h-5 w-5 text-terracotta shrink-0" />}
            </button>
          </div>
        )}

        {r.has_checkin && (
          <a
            href="https://start.campervan.cat"
            target="_blank"
            rel="noreferrer"
            className="mt-6 block bg-forest-deep hover:bg-forest transition-colors rounded-xl p-5 text-white"
            data-testid="welcome-videos-card"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <PlayCircle className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-lg">
                  Vídeos: aprende a usar tu camper
                </div>
                <div className="text-sm text-white/80 mt-0.5">
                  Techo elevable, cocina, aguas, calefacción y mucho más, en
                  vídeos de 1 minuto.
                </div>
              </div>
              <ExternalLink className="h-5 w-5 shrink-0 text-white/70" />
            </div>
          </a>
        )}

        {/* Personaliza el viaje. Se puede tocar mientras no esté pagado:
            en cuanto hay señal o extras cobrados, el backend lo bloquea. */}
        {catalogo.length > 0 && r.extras_payment_status !== "paid" && r.signal_payment_status !== "paid" && (
          <div className="bg-white border border-sand rounded-xl p-5 mb-6" data-testid="welcome-opciones">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-charcoal">Personaliza tu viaje</h3>
                <p className="text-sm text-warmgray mt-0.5">
                  Punto de recogida y devolución, kilómetros y extras. Se suman al total antes de pagar.
                </p>
              </div>
              <button
                onClick={() => setAbierto((v) => !v)}
                className="text-sm font-semibold text-forest-deep hover:underline shrink-0"
                data-testid="welcome-opciones-toggle"
              >
                {abierto ? "Ocultar" : "Ver opciones"}
              </button>
            </div>

            {(r.selected_items || []).length > 0 && !abierto && (
              <ul className="mt-3 space-y-1 text-sm text-warmgray" data-testid="welcome-opciones-resumen">
                {(r.selected_items || []).map((it, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                    <span>{Number(it.subtotal || 0).toFixed(2)} €</span>
                  </li>
                ))}
              </ul>
            )}

            {abierto && (
              <div className="mt-4">
                <CatalogPicker products={catalogo} days={dias} value={seleccion} onChange={setSeleccion} />
                <div className="mt-4 flex justify-end">
                  <Button onClick={guardarSeleccion} disabled={guardando} className="bg-forest-deep hover:bg-forest text-white" data-testid="welcome-opciones-guardar">
                    {guardando ? "Guardando..." : "Guardar opciones"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Señal al reservar: es lo primero que se pide, sin esperar al
            check-in. El resto y la fianza, antes de la entrega (ver
            backend/payment_plan.py). */}
        {r.payment_plan?.stage === "signal" && (
          <div className="mt-6 bg-forest-deep/10 border border-forest-deep/30 rounded-xl p-4" data-testid="welcome-signal-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-forest-deep shrink-0 mt-0.5" />
              <div className="text-sm text-charcoal">
                Para confirmar tu reserva falta la señal de{" "}
                <strong>{Number(r.payment_plan.signal_amount).toFixed(2)} €</strong>. El resto del alquiler
                ({Number(r.payment_plan.balance_amount).toFixed(2)} €) y la fianza ({Number(r.deposit_amount).toFixed(0)} €,
                retenida, no cobrada) se pagan unos días antes de la entrega: te avisaremos por correo.
              </div>
            </div>
            <Button
              onClick={paySignal}
              disabled={payLoading}
              size="sm"
              className="mt-3 bg-forest-deep hover:bg-forest text-white"
              data-testid="welcome-signal-pay-btn"
            >
              {payLoading ? "Abriendo el pago..." : `Pagar señal ${Number(r.payment_plan.signal_amount).toFixed(2)} €`}
            </Button>
          </div>
        )}

        {r.payment_status === "pending" && r.has_checkin && r.payment_plan?.stage !== "signal" && (
          <div className="mt-6 bg-warn/10 border border-warn/30 rounded-xl p-4" data-testid="welcome-deposit-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-warn-ink shrink-0 mt-0.5" />
              <div className="text-sm text-warn-ink">
                La fianza ({Number(r.deposit_amount || 0).toFixed(2)} €) aún está pendiente de pago.
              </div>
            </div>
            <Button
              onClick={payDeposit}
              disabled={payLoading}
              size="sm"
              className="mt-3 bg-forest-deep hover:bg-forest text-white"
              data-testid="welcome-deposit-pay-btn"
            >
              {payLoading ? "Abriendo el pago..." : "Pagar la fianza ahora"}
            </Button>
          </div>
        )}

        {r.rental_payment_status === "pending" && Number(r.total_amount || 0) > 0 && r.has_checkin && r.payment_plan?.stage !== "signal" && (
          <div className="mt-6 bg-warn/10 border border-warn/30 rounded-xl p-4" data-testid="welcome-rental-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-warn-ink shrink-0 mt-0.5" />
              <div className="text-sm text-warn-ink">
                {r.signal_payment_status === "paid"
                  ? `Señal recibida. El resto del alquiler (${Number(r.payment_plan?.balance_amount ?? r.total_amount).toFixed(2)} €) se paga junto con la fianza antes de la entrega.`
                  : `El pago del alquiler (${Number(r.total_amount).toFixed(2)} €) está pendiente.`}
              </div>
            </div>
            <Button
              onClick={() => navigate(`/checkin/${r.code}`)}
              size="sm"
              className="mt-3 bg-forest-deep hover:bg-forest text-white"
              data-testid="welcome-rental-pay-btn"
            >
              Completar pago
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
